import {
  retrieveToken,
  retrieveRefreshToken,
  saveToken,
  clearToken,
} from '../storage';
import { BSFOOD_AUTH_LOGOUT_EVENT } from '../syncSharedStoreToken';
import { markSessionLoggingOut } from '../sessionLogout';

const BASE_URL = process.env.REACT_APP_BACKEND_ENDPOINT;
const REFRESH_PATH = '/api/auth/refresh';

// The sentences more than one endpoint gives, defined once so no module keeps
// the old one the day one of them changes. A sentence a single endpoint says
// stays inline at its call site: there is nothing to keep in sync.
export const OPERAZIONE_NON_CONSENTITA = 'Operazione non consentita.';
export const SALVATAGGIO_DATI_NON_CORRETTI =
  'Salvataggio non riuscito: i dati non sono corretti.';
export const REGISTRAZIONE_DATI_NON_CORRETTI =
  'Registrazione non riuscita: dati non corretti.';
export const REGISTRAZIONE_EMAIL_DUPLICATA =
  'Registrazione non riuscita: esiste già un utente registrato con lo stesso indirizzo email indicato.';
export const CARICAMENTO_IMMAGINE_NON_VALIDA =
  "Caricamento fallito: il formato dell'immagine o la dimensione dell'immagine non sono corretti.";
export const RICHIESTA_NON_VALIDA = 'Richiesta non valida.';
export const SERVIZIO_NON_DISPONIBILE =
  'Servizio temporaneamente non disponibile. Riprovare più tardi.';
export const ERRORE_GENERICO =
  'Si è verificato un errore. Operazione non riuscita.';
// What an operator reads when the request never reached the backend, or when
// the load balancer answered instead of it. Neither case is a backend failure:
// a pod is being replaced, or the machine has no network at all.
export const NESSUNA_CONNESSIONE =
  'Nessuna connessione a internet. Verificare la rete e riprovare.';
export const SERVIZIO_IN_AGGIORNAMENTO =
  'Il servizio si sta aggiornando. Riprovare tra un minuto.';

// Authentication endpoints: never attempt a refresh on these, it would recurse.
const SKIP_REFRESH_PATHS = [
  '/api/auth/refresh',
  '/api/login',
  '/api/login/backoffice',
  '/api/auth/oauth',
];

// One refresh call, shared by every concurrent request.
let refreshInFlight = null;
let authFailureHandled = false;

// One retry, far enough after the failure to land on the other side of a pod
// swap. An estimate, not a measurement: nothing in the tests proves it matches
// a real rollout window, so a staging observation may move it.
const RETRY_DELAY_MS = 2000;

// Set on the rejection of a `fetch`, and only there. The property travels on
// the error object itself, so it survives every rethrow along the chain.
const TRANSPORT_FAILURE = '__bsfoodTransportFailure';

// How `fetch` reports that the request never reached the server. Every browser
// words that rejection differently — Chrome, Firefox and Safari each have their
// own sentence — so the message says nothing portable and the type does.
const isFetchRejection = (error) => error instanceof TypeError;

/**
 * Marks the rejection of a `fetch` as a transport failure and rethrows it, for
 * use as a `.catch` handler at the fetch boundary.
 */
const markTransportFailure = (error) => {
  if (isFetchRejection(error)) {
    error[TRANSPORT_FAILURE] = true;
  }
  throw error;
};

/**
 * Whether the request never reached the server. The type alone does not answer
 * that: the wrappers' outer `.catch` also sees whatever the `.then` chain
 * raised, and a frontend bug dereferencing an undefined field is a `TypeError`
 * too. Only the fetch boundary marks its own rejection, so a code defect keeps
 * the generic sentence and the `0` sentinel instead of being shown to the
 * operator as a pod swap.
 */
export const isTransportError = (error) =>
  isFetchRejection(error) && error[TRANSPORT_FAILURE] === true;

/**
 * The sentence for a request that never got an answer. `navigator.onLine` is
 * unreliable to claim you are online and reliable enough to claim you are not,
 * which is the only direction used here: a customer whose own connection went
 * down is not told that the service is updating.
 */
export const transportErrorMessage = () =>
  typeof navigator !== 'undefined' && navigator.onLine === false
    ? NESSUNA_CONNESSIONE
    : SERVIZIO_IN_AGGIORNAMENTO;

// The three statuses no bs-food route returns on its own: in this frontend they
// always mean the load balancer in front of it.
const LOAD_BALANCER_STATUSES = [502, 503, 504];

export const isLoadBalancerStatus = (status) =>
  LOAD_BALANCER_STATUSES.includes(status);

const delay = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

/**
 * A 503 says no target received the request, so replaying it cannot duplicate a
 * record whatever the method. A 502 or a 504 leaves open whether the target
 * already ran it, so only a read is replayed.
 */
const isRetryableStatus = (status, method) => {
  if (status === 503) return true;
  if (status !== 502 && status !== 504) return false;
  return String(method ?? 'GET').toUpperCase() === 'GET';
};

/**
 * `fetch` plus a single retry, `RETRY_DELAY_MS` after a transport failure or a
 * retryable load-balancer status. It wraps one fetch, so a call going through
 * `apiFetch` during a 401 gets its own retry on the original request, on the
 * refresh POST and on the replay: six requests at worst, and only when a pod
 * swap coincides with an expired token.
 */
export const fetchWithRetry = async (url, options = {}) => {
  let response;
  try {
    response = await fetch(url, options);
  } catch (error) {
    if (!isFetchRejection(error)) throw error;
    await delay(RETRY_DELAY_MS);
    return fetch(url, options).catch(markTransportFailure);
  }

  if (!isRetryableStatus(response.status, options.method)) {
    return response;
  }
  await delay(RETRY_DELAY_MS);
  return fetch(url, options).catch(markTransportFailure);
};

async function syncStoreToken(token) {
  try {
    const mod = await import('store/Store');
    mod.default.getState().setToken(token);
  } catch (e) {
    // remote store (MF) not available: ignore
  }
}

function handleAuthFailure() {
  if (authFailureHandled) return;
  authFailureHandled = true;
  // Mark the logout BEFORE the call is rejected, so that the calling
  // component's ErrorDialog (shown from its catch) is suppressed and only the
  // central session-expired modal is left.
  markSessionLoggingOut();
  clearToken();
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new CustomEvent(BSFOOD_AUTH_LOGOUT_EVENT));
  }
}

function requestRefresh() {
  if (refreshInFlight) return refreshInFlight;

  const refreshToken = retrieveRefreshToken();
  if (!refreshToken) {
    return Promise.resolve(null);
  }

  refreshInFlight = fetchWithRetry(BASE_URL + REFRESH_PATH, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ refreshToken }),
  })
    .then((res) => (res.ok ? res.json() : null))
    .then((data) => {
      const newToken = data && data.token ? data.token : null;
      if (newToken) {
        saveToken(newToken);
        syncStoreToken(newToken);
        authFailureHandled = false;
      }
      return newToken;
    })
    .catch(() => null)
    .finally(() => {
      refreshInFlight = null;
    });

  return refreshInFlight;
}

function withAuthHeaders(options, token) {
  const headers = { ...(options && options.headers ? options.headers : {}) };
  if (token) {
    headers.Authorization = 'Bearer ' + token;
  }
  return { ...options, headers };
}

function isRefreshableUrl(url) {
  const u = String(url);
  return !SKIP_REFRESH_PATHS.some((p) => u.includes(p));
}

/**
 * Drop-in for `fetch` on authenticated calls: injects the current Bearer and,
 * on a 401, runs a single refresh (single-flight) and replays the request once.
 * When the session is over it clears the tokens and signals the logout
 * (bsfood:auth-logout).
 */
export const apiFetch = async (url, options = {}) => {
  const token = retrieveToken();
  const response = await fetchWithRetry(url, withAuthHeaders(options, token));

  if (response.status !== 401 || !isRefreshableUrl(url)) {
    return response;
  }

  const newToken = await requestRefresh();
  if (!newToken) {
    // Session over: access token and refresh token are both invalid.
    // handleAuthFailure marks the logout, clears the tokens and notifies the
    // central modal (HostSessionExpiredDialog). The original 401 response goes
    // back to the caller: the call resolves, so the component's spinner stops
    // instead of spinning forever, and its catch runs — but the child's
    // ErrorDialog is suppressed by the logout flag, leaving only the central
    // modal on screen.
    handleAuthFailure();
    return response;
  }

  const replay = await fetchWithRetry(url, withAuthHeaders(options, newToken));
  if (replay.status === 401) {
    // The token we just obtained is not accepted either, so the session is
    // over. A 401 on a protected route always means a rejected token: the only
    // 401 bs-food raises on its own comes from the auth controllers, and those
    // are in SKIP_REFRESH_PATHS, so there is no application case to mistake
    // for this one. Without this the caller shows its own ErrorDialog and the
    // operator stays in the panel holding a dead token, with no central
    // session-expired modal.
    handleAuthFailure();
  }
  return replay;
};

/**
 * How both wrappers reject when the request never reached the server. The
 * statuses are sentinels no endpoint returns — -1 the network, 0 anything else
 * thrown while handling the response — so a caller can tell a transport
 * failure from a refusal.
 */
const rejectTransportError = (reject, error) => {
  if (isTransportError(error)) {
    return reject(new ApiError(-1, transportErrorMessage()));
  }
  return reject(new ApiError(0, ERRORE_GENERICO));
};

export const wrapFetch = (apiRequest, apiErrorFunc) => {
  return new Promise((resolve, reject) => {
    apiRequest
      .then((response) => {
        const status = response.status;
        if (status === 200 || status === 201) {
          if (
            response.headers.get('Content-Type')?.includes('application/json')
          ) {
            return resolve(response.json());
          }
          return resolve(response);
        }
        if (status === 204) {
          return resolve(response);
        }
        if (isLoadBalancerStatus(status)) {
          // The retry is already spent: the load balancer is still answering,
          // so the honest sentence is that the service is coming back, not
          // that the request was refused.
          return reject(new ApiError(status, SERVIZIO_IN_AGGIORNAMENTO));
        }
        reject(apiErrorFunc(status));
      })
      .catch((error) => rejectTransportError(reject, error));
  });
};

// A code, not a sentence. The sonde registry routes put `ERR-003` / `ERR-004`
// in `details` as a contract with their clients (bs-safe-app maps them), and
// showing one to an operator in place of a description would be worse than the
// generic bucket: it is there to be recognised, not to be displayed.
const CODICE_ERRORE = /^ERR-\d+$/;

/**
 * Whether an entry of `details` is a code rather than a sentence. Case and
 * surrounding spaces are ignored: an entry the server wrote as `err-005` is
 * still a code, and letting it through as the sentence to display would print
 * the protocol to the operator.
 */
export const isCodiceErrore = (valore) =>
  CODICE_ERRORE.test(
    String(valore ?? '')
      .trim()
      .toUpperCase(),
  );

// Codes whose following `details` entries are parameters, not a sentence, so
// the sentence to show is `message`. ERR-007 / ERR-008 refuse the assignment of
// a probe with no readings in the last 7 days / not transmitting since the ISO instant in
// `details[1]` (bs-modules #428). Other codes, like ERR-001, can be followed by
// the sentence itself, so this is a list and not a rule on every code.
const CODICI_CON_PARAMETRI = ['ERR-007', 'ERR-008'];

export const wrapFetchWithDetailedErrors = (apiRequest, apiErrorFunc) => {
  return new Promise((resolve, reject) => {
    apiRequest
      .then((response) => {
        const status = response.status;
        if (status >= 200 && status < 300) {
          if (
            response.headers.get('Content-Type')?.includes('application/json')
          ) {
            return response.json().then((data) => resolve(data));
          } else {
            return resolve(response);
          }
        }

        if (status >= 400 && status < 500) {
          return response.text().then((errorMessage) => {
            try {
              const parsedError = JSON.parse(errorMessage);
              const dettagli = Array.isArray(parsedError.details)
                ? parsedError.details
                : [];
              // `message` is the generic bucket from bs-food's exception
              // handler — «Richiesta non valida», «Record gia' presente» — and
              // the contract declares it explicitly unfit to show an operator:
              // the sentence to show lives in `details`. Prefixing it would
              // read «Richiesta non valida. La sonda aabbccddee10 alimenta
              // gia'...», the language of the protocol in front of the
              // description of what happened.
              //
              // But not every entry in `details` is a sentence: the first one
              // that is not a code wins, and `message` is the fallback when
              // there is none. That is the case on the registry routes, where
              // the caller reads the code from the third argument anyway and
              // shows a confirmation of its own instead of this text.
              //
              // Some codes are followed by their parameters, not by a
              // sentence: see CODICI_CON_PARAMETRI.
              const frase = CODICI_CON_PARAMETRI.includes(dettagli[0])
                ? undefined
                : dettagli.find((dettaglio) => !isCodiceErrore(dettaglio));
              const detailedMessage = frase ?? parsedError.message;
              // `details` also travels whole, as the third argument: when the
              // server puts a code (`ERR-003`) there, the caller has to be able
              // to read it instead of hunting for it as a substring inside an
              // Italian message. The errorFunctions that have no use for it
              // ignore it.
              reject(
                apiErrorFunc(status, detailedMessage, parsedError.details),
              );
            } catch (e) {
              reject(apiErrorFunc(status, errorMessage));
            }
          });
        }

        if (isLoadBalancerStatus(status)) {
          // Nothing in the backend failed: the pod behind the load balancer was
          // being replaced, and the 'Errore interno del server' below would say
          // otherwise.
          return reject(new ApiError(status, SERVIZIO_IN_AGGIORNAMENTO));
        }

        if (status >= 500) {
          return response.text().then((errorMessage) => {
            reject(
              apiErrorFunc(
                status,
                'Errore interno del server. Riprovare più tardi.',
              ),
            );
          });
        }
        reject(apiErrorFunc(status));
      })
      .catch((error) => rejectTransportError(reject, error));
  });
};

export class ApiError extends Error {
  constructor(status, message) {
    super(message ? message : ERRORE_GENERICO);
    this.status = status;
  }
}

/**
 * Builds the `apiErrorFunc` the two wrappers reject with, so that an endpoint
 * declares only what it says differently from everyone else.
 *
 * Precedence, highest first:
 *   1. the server's own sentence, when `wrapFetchWithDetailedErrors` extracted
 *      one — it describes what actually happened, the static text cannot;
 *   2. `overrides[status]`, the endpoint's own text for that status;
 *   3. `OPERAZIONE_NON_CONSENTITA` on a 403, the answer every endpoint gives;
 *   4. nothing, and `ApiError` falls back to its generic sentence.
 *
 * `||` and not `??`: an empty string from the server is not a message to show,
 * it has to fall through to the endpoint's text.
 *
 *   wrapFetch(apiFetch(URL, options), apiError())
 *   wrapFetch(apiFetch(URL, options), apiError({ 404: 'Gruppo non trovato.' }))
 */
export const apiError =
  (overrides = {}) =>
  (status, serverMessage = null) =>
    new ApiError(
      status,
      serverMessage ||
        overrides[status] ||
        (status === 403 ? OPERAZIONE_NON_CONSENTITA : null),
    );
